import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { TURN_OUTCOME_TONE } from "../status/turnOutcomeTone";
import type {
  StepOutputPayload,
  ThreadEntry,
  ToolCallPayload,
  TurnFinishedPayload,
  TurnRequestedPayload,
} from "../../../api/threads/liveMessages";
import { appPaths } from "../../../shared/routing/appPaths";
import ErrorNotice from "../../../errors/ErrorNotice";
import CopyReference from "../../../ui/content/CopyReference";
import MarkdownRenderer from "../../../ui/content/MarkdownRenderer";
import DefinitionListItem from "../../../ui/layout/DefinitionListItem";
import { capabilityLabel, hintText } from "../hosting/routeFitText";
import { showCurrentStep } from "../page/currentStep/currentStepElement";
import { useThreadContext } from "../page/threadPageContext";
import { AgentSetupSummary, TryWithDmwOnly } from "./AgentSetupView";
import AnnotationSetView from "../../annotation/AnnotationSetView";
import { SelectionToChat } from "../../chatReferences";
import InstanceDataOutput from "./InstanceDataOutput";
import type { TurnStep } from "./logBlocks";
import { recordedProblemError } from "./recordedProblems";
import { ToolValueBody } from "./ToolTurnEntries";
import { routeLabel } from "./routeLabels";
import ToolCallDetails from "./ToolCallDetails";
import { hasUsage } from "./toolValues";
import VersionsUsedList, { CostLine, UsageLine } from "./VersionsUsedList";
import Chip from "../../../ui/feedback/Chip";
import SourceView from "../../../ui/content/source/SourceView";

/**
 * Entries of agent turns (threads.md T-2, T-4, T-12): the request with its frozen input and
 * exact versions, tool calls (collapsed, mcp-endpoint.md "What is recorded"), step outputs,
 * and the result with the agent's answer, usage and what ran it.
 */

export function TurnRequestedBody({ payload }: { payload: TurnRequestedPayload }) {
  const { t } = useTranslation();
  const { stepName } = useThreadContext();
  const what = payload.turn_kind === "step" ? stepName(payload.step_key, payload.step) : t("threads.turn.reply");
  const unknown = unknownCapabilities(payload);
  const talk = payload.talk ?? null;
  return (
    <>
      <span>
        {t(talk === null ? "threads.turn.askedAgent" : "threads.turn.talked", { what })}
        {payload.covers.length === 0 ? null : ` ${t("threads.turn.covers", { entries: payload.covers.map((number) => `#${String(number)}`).join(", ") })}`}
      </span>
      {talk === null ? null : (
        <>
          <p className="mt-1 whitespace-pre-wrap text-sm">{talk.request}</p>
          <p className="app-text-muted text-xs">{t(`threads.turn.talkContinuations.${talk.continuation}`)}</p>
        </>
      )}
      {unknown.length === 0 ? null : (
        <p className="app-warning-highlight-text mt-1 text-xs">
          {t("threads.turn.capabilitiesUnknown", { capabilities: unknown.map(({ capability, hint }) => (hint === null ? capabilityLabel(t, capability) : `${capabilityLabel(t, capability)} (${hintText(t, hint)})`)).join(", ") })}
        </p>
      )}
      <details className="mt-1">
        <summary className="app-link cursor-pointer text-[11px]">{t("threads.turn.details")}</summary>
        <div className="mt-2 space-y-3">
          <dl className="space-y-1 text-xs">
            <DefinitionListItem term={t("threads.turn.route")}>{routeLabel(payload.route, t)}</DefinitionListItem>
            <DefinitionListItem term={t("threads.turn.permissionProfile")}>{payload.permission_profile}</DefinitionListItem>
            <DefinitionListItem term={t("threads.turn.session")}>{t(`threads.turn.sessionModes.${payload.session.mode}`)}</DefinitionListItem>
            <DefinitionListItem term={t("threads.turn.tools")} valueClassName="font-mono">
              {[...payload.tools.dmw, ...payload.tools.skill_servers, ...payload.tools.agent_skills].join(", ") || "–"}
            </DefinitionListItem>
            <DefinitionListItem term={t("threads.turn.basedOn")} valueClassName="font-mono">#{payload.based_on_entry}</DefinitionListItem>
          </dl>
          <VersionsUsedList versions={payload.versions_used} />
          {payload.input.map((block, index) => (
            <details key={`${block.purpose}-${String(index)}`}>
              <summary className="app-text-muted cursor-pointer text-[11px]">{t(`threads.turn.inputPurposes.${block.purpose}`)}</summary>
              <SourceView label={t(`threads.turn.inputPurposes.${block.purpose}`)} language="plaintext" value={block.text} className="mt-1" />
            </details>
          ))}
        </div>
      </details>
    </>
  );
}

/** The requirements no fact stated when the turn was prepared, with an agent's estimate if any: it ran with a warning (D17). */
function unknownCapabilities(payload: TurnRequestedPayload): { readonly capability: string; readonly hint: boolean | number | string | null }[] {
  const unknown = new Map<string, boolean | number | string | null>();
  for (const requirement of payload.capabilities?.requirements ?? []) {
    if (requirement.outcome === "unknown" && !unknown.has(requirement.capability)) unknown.set(requirement.capability, requirement.hint?.value ?? null);
  }
  return [...unknown].map(([capability, hint]) => ({ capability, hint }));
}

export function TurnFinishedBody({ entry, payload, turnStep = null }: { entry: ThreadEntry; payload: TurnFinishedPayload; turnStep?: TurnStep | null }) {
  const { t } = useTranslation();
  const setup = payload.agent_setup ?? null;
  return (
    <div className="space-y-2">
      {payload.agent_messages.map((text, index) => (
        <MarkdownRenderer key={`${entry.number.toString()}-${String(index)}`} content={text} className="app-markdown text-sm" />
      ))}
      {payload.problem === null ? null : <ErrorNotice error={recordedProblemError(payload.problem)} />}
      {payload.redacted_patterns.length === 0 ? null : (
        <p className="app-status-text-warning text-xs">{t("threads.turn.redacted", { patterns: payload.redacted_patterns.join(", ") })}</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {payload.outcome === "completed" ? null : (
          <Chip tone={TURN_OUTCOME_TONE[payload.outcome]}>{t(`threads.turn.outcomes.${payload.outcome}`)}</Chip>
        )}
        {hasUsage(payload.usage) ? <CostLine usage={payload.usage} /> : null}
        {setup === null ? null : <Chip size="compact">{t(`threads.turn.agentSetup.${setup.setup}`)}</Chip>}
      </div>
      {payload.outcome === "failed" && setup?.setup === "extend" ? <TryWithDmwOnly turnStep={turnStep} /> : null}
      <details>
        <summary className="app-link cursor-pointer text-[11px]">{t("threads.turn.details")}</summary>
        <dl className="mt-2 space-y-1 text-xs">
          <DefinitionListItem term={t("threads.turn.outcome")}>{t(`threads.turn.outcomes.${payload.outcome}`)}</DefinitionListItem>
          {payload.agent === null ? null : (
            <DefinitionListItem term={t("threads.turn.agent")}>
              {[payload.agent.name, payload.agent.version, payload.agent.model_id].filter((part) => part !== null).join(", ")}
            </DefinitionListItem>
          )}
          <DefinitionListItem term={t("threads.usage.title")}><UsageLine usage={payload.usage} /></DefinitionListItem>
          <DefinitionListItem term={t("threads.turn.answers")} valueClassName="font-mono">#{payload.request_entry}</DefinitionListItem>
          {payload.output_entry === null ? null : (
            <DefinitionListItem term={t("threads.turn.output")} valueClassName="font-mono">#{payload.output_entry}</DefinitionListItem>
          )}
          <DefinitionListItem term={t("threads.turn.providerContact")}>{t(`threads.turn.providerContacts.${payload.provider_contact}`)}</DefinitionListItem>
          {payload.session_use === null ? null : (
            <DefinitionListItem term={t("threads.turn.session")}>{t(`threads.turn.sessionModes.${sessionModeOf(payload.session_use)}`)}</DefinitionListItem>
          )}
          <DefinitionListItem term={t("threads.turn.permissionProfile")}>{payload.permission_profile}</DefinitionListItem>
          {setup === null ? null : <AgentSetupSummary setup={setup} />}

        </dl>
        {payload.local_tool_calls.length === 0 ? null : (
          <ul className="mt-2 space-y-1 text-xs">
            {payload.local_tool_calls.map((call) => (
              <li key={call.tool_call_id} className="app-text-muted">
                {t("threads.turn.localToolCall", { title: call.title, kind: call.tool_kind, status: call.status })}
                {call.locations.length === 0 ? null : ` · ${call.locations.join(", ")}`}
              </li>
            ))}
          </ul>
        )}
      </details>
    </div>
  );
}

/** A DMW tool the agent called, collapsed by default: its arguments and what it read. */
export function ToolCallBody({ payload, threadId }: { payload: ToolCallPayload; threadId?: string }) {
  const { t } = useTranslation();
  return (
    <details>
      <summary className="cursor-pointer">
        {t("threads.toolCall.summary", { tool: payload.tool, outcome: payload.outcome, milliseconds: payload.duration_milliseconds })}
      </summary>
      <ToolCallDetails payload={payload} threadId={threadId} />
    </details>
  );
}

/** Selecting text in what a Thread entry shows asks the agent about the entry `<thread>#n` (F96): one boundary for every view of an entry's output. */
export function AskAboutEntry({ entry, children }: { entry: number; children: ReactNode }) {
  const { t } = useTranslation();
  const { threadId, overview } = useThreadContext();
  return (
    <SelectionToChat projectId={overview.projectId} subject={{ reference: `${threadId}#${String(entry)}`, name: t("chatReferences.entry", { number: entry }) }}>
      {children}
    </SelectionToChat>
  );
}

/** A step's output; with its entry number, selecting text in it asks the agent about that entry. */
export function StepOutputBody({ payload, entry }: { payload: StepOutputPayload; entry?: number }) {
  const { t } = useTranslation();
  const { outputsInStep, currentStep } = useThreadContext();
  // The current step shows what it reads, such as the result it asks to accept: the log points there, once (F104).
  if (entry !== undefined && outputsInStep.has(entry)) {
    return <button type="button" className="app-link text-xs underline" onClick={() => { showCurrentStep(currentStep); }}>{t("threads.output.inCurrentStep")}</button>;
  }
  const body = <StepOutputContent payload={payload} entry={entry} />;
  return entry === undefined ? body : <AskAboutEntry entry={entry}>{body}</AskAboutEntry>;
}

function StepOutputContent({ payload, entry }: { payload: StepOutputPayload; entry: number | undefined }) {
  const { t } = useTranslation();
  const output = payload.output;
  if (output.output_kind === "value") {
    return <ToolValueBody part={payload.part} output={output} entry={entry} />;
  }
  if (output.output_kind === "change_set") {
    return (
      <p className="flex flex-wrap items-center gap-x-2 text-sm">
        <span>{t("threads.output.changeSetProposed")}</span>
        <Link to={output.collection === "corpus" ? appPaths.importProposal(output.change_set_id) : appPaths.review(output.change_set_id)} className="app-link underline">
          {t("threads.output.openReview")}
        </Link>
        <CopyReference value={`${output.change_set_id}@${output.head}`} />
      </p>
    );
  }
  if (output.output_kind === "instance_data") {
    return <InstanceDataOutput output={output} entry={entry} />;
  }
  return <AnnotationSetView output={output} />;
}

function sessionModeOf(use: NonNullable<TurnFinishedPayload["session_use"]>): TurnRequestedPayload["session"]["mode"] {
  switch (use) {
    case "continued":
      return "continue";
    case "resumed":
      return "resume";
    case "fresh":
    case "isolated":
      return use;
  }
}
